import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { timeAgo } from './UnclaimedWorkList'

const SOURCE_LABELS = {
  voicemail: 'Voicemail',
  sms: 'SMS',
  phone_call: 'Phone Call',
  email: 'Email',
  field_observation: 'Field Observation',
  walk_in: 'Walk-in',
  aspire_work_order: 'Aspire WO',
  customer_portal: 'Customer Portal',
  herbicide_review: 'Herbicide Review',
}

const TRIAGE_TYPES = [
  { value: 'service_request', label: 'Service request' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'info_request', label: 'Info request' },
  { value: 'internal', label: 'Internal' },
  { value: 'vendor_invoice_request', label: 'Vendor invoice' },
  { value: 'other', label: 'Other' },
]

const TRIAGE_SERVICES = ['Maintenance', 'Irrigation', 'Planting', 'Hardscape', 'Chemical', 'Billing/AR', 'Other']
const TRIAGE_PRIORITIES = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
]

const BLANK_DRAFTS = {
  task: { description: '', assignees: [], assigneeQuery: '', due_date: '', tags: '' },
  question: { text: '', askPeople: [], askQuery: '', need_by: '' },
  blocker: { description: '', severity: 'med', unblock_with: '', workaround: '' },
  decision: { text: '', context_question: '' },
}

const IconVoicemail = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <circle cx="5.5" cy="11.5" r="4.5"/><circle cx="18.5" cy="11.5" r="4.5"/><line x1="5.5" y1="16" x2="18.5" y2="16"/>
  </svg>
)
const IconSms = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)
const IconPhone = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72"/>
  </svg>
)
const IconEmail = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
)

const SOURCE_ICONS = {
  voicemail: IconVoicemail,
  sms: IconSms,
  phone_call: IconPhone,
  email: IconEmail,
  field_observation: IconPhone,
  walk_in: IconPhone,
  aspire_work_order: IconPhone,
  customer_portal: IconPhone,
  herbicide_review: IconPhone,
}

const IconClaim = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
)
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const IconTrash = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
)
const IconFile = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
  </svg>
)
const IconInbox = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
  </svg>
)

function userName(user) {
  if (!user) return ''
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.displayName || user.name || ''
}

function teamMemberName(member) {
  if (!member) return ''
  return member.displayName || [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email || 'Team member'
}

function personKey(person) {
  return person?.user || person?.employee || person?.name || ''
}

function personFromMember(member) {
  if (!member) return null
  return {
    user: member.user ? String(member.user) : '',
    employee: member._id ? String(member._id) : '',
    name: teamMemberName(member),
  }
}

function artifactPeopleLabel(item, pluralField, singleField, fallback = '') {
  const names = Array.isArray(item?.[pluralField]) ? item[pluralField].filter(Boolean) : []
  return names.length ? names.join(', ') : item?.[singleField] || fallback
}

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB'
  return (bytes / 1048576).toFixed(1) + ' MB'
}

function formatDueDate(dateStr) {
  if (!dateStr) return 'None'
  const due = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
  const diffDays = Math.round((dueDay - today) / 86400000)
  if (diffDays < 0) return 'Overdue'
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function normalizeDateForInput(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toISOString().slice(0, 10)
}

function normalizeTriage(item, currentUser) {
  const ownerName = item?.triage?.owner_name || item?.assigned_to_name || userName(currentUser)
  return {
    type: item?.triage?.type || 'service_request',
    service: item?.triage?.service || item?.service_types?.[0] || 'Maintenance',
    priority: item?.triage?.priority || item?.priority || 'normal',
    owner_name: ownerName,
    due_date: normalizeDateForInput(item?.triage?.due_date || item?.due_date),
  }
}

function sectionCount(items, doneStatus) {
  const done = items.filter(item => item.status === doneStatus).length
  const open = Math.max(items.length - done, 0)
  if (!items.length) return '0'
  if (!done) return `${open} open`
  return `${open} open / ${done} done`
}

export default function UnclaimedWorkDetail({ item, url, currentUser, currentUserId, onUpdate, workMode = false }) {
  const [assignSearch, setAssignSearch] = useState('')
  const [assignResults, setAssignResults] = useState([])
  const [showAssignDropdown, setShowAssignDropdown] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [aspireId, setAspireId] = useState('')
  const [loading, setLoading] = useState(false)
  const [workNotes, setWorkNotes] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [triage, setTriage] = useState(() => normalizeTriage(item, currentUser))
  const [activeComposer, setActiveComposer] = useState(null)
  const [drafts, setDrafts] = useState(BLANK_DRAFTS)
  const [activeBids, setActiveBids] = useState([])
  const [teamMembers, setTeamMembers] = useState([])
  const [bidSearch, setBidSearch] = useState('')
  const [selectedBid, setSelectedBid] = useState(null)
  const [showBidDropdown, setShowBidDropdown] = useState(false)
  const [openTriageMenu, setOpenTriageMenu] = useState(null)
  const searchTimer = useRef(null)
  const notesTimer = useRef(null)

  useEffect(() => {
    setAssignSearch('')
    setAssignResults([])
    setShowAssignDropdown(false)
    setSelectedEmployee(null)
    setAspireId(item?.aspire_work_ticket_id || '')
    setWorkNotes(item?.work_notes || '')
    setLastSavedAt(item?.work_notes_updated_at || null)
    setTriage(normalizeTriage(item, currentUser))
    setActiveComposer(null)
    setDrafts(BLANK_DRAFTS)
    setBidSearch('')
    setSelectedBid(null)
    setShowBidDropdown(false)
    setOpenTriageMenu(null)
  }, [item?._id, currentUser])

  useEffect(() => {
    if (!url || !workMode) return
    axios.get(`${url}/api/hmi/dashboard/active-bids`)
      .then(res => {
        if (res.data?.success) setActiveBids(res.data.data || [])
      })
      .catch(() => {})
    axios.get(`${url}/api/hmi/unclaimed-work/employees`, { params: { q: '' } })
      .then(res => {
        if (res.data?.success) setTeamMembers(res.data.data || [])
      })
      .catch(() => {})
  }, [url, workMode])

  useEffect(() => {
    if (!url || assignSearch.length < 2) {
      setAssignResults([])
      return undefined
    }
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      axios.get(`${url}/api/hmi/unclaimed-work/employees`, { params: { q: assignSearch } })
        .then(res => {
          if (res.data?.success) {
            setAssignResults(res.data.data)
            setShowAssignDropdown(true)
          }
        })
        .catch(() => {})
    }, 300)
    return () => clearTimeout(searchTimer.current)
  }, [assignSearch, url])

  const patchItem = useCallback(async (updates, options = {}) => {
    if (!item || !url) return null
    if (!options.silent) setLoading(true)
    try {
      const res = await axios.patch(`${url}/api/hmi/unclaimed-work/${item._id}`, updates)
      if (onUpdate) onUpdate(res.data?.data)
      return res.data?.data
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message
      alert(message || 'Failed to update item')
      return null
    } finally {
      if (!options.silent) setLoading(false)
    }
  }, [item, url, onUpdate])

  useEffect(() => {
    if (!item || workNotes === (item.work_notes || '')) return undefined
    clearTimeout(notesTimer.current)
    setSavingNotes(true)
    notesTimer.current = setTimeout(async () => {
      const saved = await patchItem({ work_notes: workNotes }, { silent: true })
      if (saved) setLastSavedAt(saved.work_notes_updated_at || new Date().toISOString())
      setSavingNotes(false)
    }, 800)
    return () => clearTimeout(notesTimer.current)
  }, [workNotes, item, patchItem])

  const updateTriage = useCallback((field, value) => {
    const next = { ...triage, [field]: value }
    setTriage(next)
    patchItem({ triage: next }, { silent: true })
  }, [triage, patchItem])

  const handleAssign = useCallback(() => {
    if (!selectedEmployee) return
    patchItem({
      state: 'assigned',
      assigned_to_employee: selectedEmployee._id,
      assigned_to_name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim(),
      triage: {
        ...triage,
        owner_name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim(),
      },
    })
  }, [selectedEmployee, patchItem, triage])

  const handleClaim = useCallback(() => {
    patchItem({
      state: 'assigned',
      assigned_to: currentUserId,
      triage: {
        ...triage,
        owner_name: triage.owner_name || userName(currentUser),
      },
    })
  }, [currentUserId, currentUser, patchItem, triage])

  const handleComplete = useCallback(() => {
    const openBlockers = (item?.blockers || []).filter(b => b.status !== 'resolved')
    if (openBlockers.length > 0) {
      alert(`Resolve or remove ${openBlockers.length} open blocker${openBlockers.length === 1 ? '' : 's'} before completing this item.`)
      return
    }
    patchItem({ state: 'completed' })
  }, [item, patchItem])

  const handleLinkAspire = useCallback(() => {
    if (!aspireId.trim()) return
    patchItem({ aspire_work_ticket_id: aspireId.trim() })
  }, [aspireId, patchItem])

  const handleArchive = useCallback(() => {
    patchItem({ state: 'archived' })
  }, [patchItem])

  const addArtifact = useCallback(async (type, payload) => {
    if (!item || !url) return null
    setLoading(true)
    try {
      const res = await axios.post(`${url}/api/hmi/unclaimed-work/${item._id}/artifacts`, { type, ...payload })
      if (onUpdate) onUpdate(res.data?.data)
      return res.data?.data
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message
      alert(message || 'Failed to add work item')
      return null
    } finally {
      setLoading(false)
    }
  }, [item, url, onUpdate])

  const updateArtifact = useCallback(async (artifactType, artifactId, payload) => {
    if (!item || !url) return
    try {
      const res = await axios.patch(`${url}/api/hmi/unclaimed-work/${item._id}/artifacts/${artifactType}/${artifactId}`, payload)
      if (onUpdate) onUpdate(res.data?.data)
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message
      alert(message || 'Failed to update work item')
    }
  }, [item, url, onUpdate])

  const deleteArtifact = useCallback(async (artifactType, artifactId) => {
    if (!item || !url) return
    if (!window.confirm('Remove this item?')) return
    try {
      const res = await axios.delete(`${url}/api/hmi/unclaimed-work/${item._id}/artifacts/${artifactType}/${artifactId}`)
      if (onUpdate) onUpdate(res.data?.data)
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message
      alert(message || 'Failed to remove work item')
    }
  }, [item, url, onUpdate])

  const handleLinkBid = useCallback(async () => {
    if (!item || !url || !selectedBid) return
    setLoading(true)
    try {
      const res = await axios.post(`${url}/api/hmi/unclaimed-work/${item._id}/link-bid`, {
        projectId: selectedBid._id,
      })
      if (onUpdate) onUpdate(res.data?.data)
      setSelectedBid(null)
      setBidSearch('')
      setShowBidDropdown(false)
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message
      alert(message || 'Failed to link bid project')
    } finally {
      setLoading(false)
    }
  }, [item, url, selectedBid, onUpdate])

  const setDraftValue = useCallback((type, key, value) => {
    setDrafts(prev => ({ ...prev, [type]: { ...prev[type], [key]: value } }))
  }, [])

  const resetDraft = useCallback((type) => {
    setDrafts(prev => ({ ...prev, [type]: BLANK_DRAFTS[type] }))
  }, [])

  const submitComposer = useCallback(async (type, keepOpen = false) => {
    const draft = drafts[type]
    let payload = null
    if (type === 'task') {
      if (!draft.description.trim()) return
      const assignees = draft.assignees || []
      payload = {
        description: draft.description.trim(),
        assignee: assignees[0]?.user || undefined,
        assignee_employee: assignees[0]?.employee || undefined,
        assignee_name: assignees[0]?.name || triage.owner_name || userName(currentUser),
        assignees: assignees.map(person => person.user).filter(Boolean),
        assignee_employees: assignees.map(person => person.employee).filter(Boolean),
        assignee_names: assignees.map(person => person.name).filter(Boolean),
        due_date: draft.due_date || undefined,
        tags: draft.tags ? draft.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      }
    }
    if (type === 'question') {
      if (!draft.text.trim()) return
      const askPeople = draft.askPeople || []
      payload = {
        text: draft.text.trim(),
        ask_user: askPeople[0]?.user || undefined,
        ask_employee: askPeople[0]?.employee || undefined,
        ask: askPeople.map(person => person.name).filter(Boolean).join(', '),
        ask_users: askPeople.map(person => person.user).filter(Boolean),
        ask_employees: askPeople.map(person => person.employee).filter(Boolean),
        ask_names: askPeople.map(person => person.name).filter(Boolean),
        need_by: draft.need_by || undefined,
      }
    }
    if (type === 'blocker') {
      if (!draft.description.trim()) return
      payload = {
        description: draft.description.trim(),
        severity: draft.severity || 'med',
        unblock_with: draft.unblock_with.trim(),
        workaround: draft.workaround.trim(),
      }
    }
    if (type === 'decision') {
      if (!draft.text.trim()) return
      payload = {
        text: draft.text.trim(),
        context_question: draft.context_question || undefined,
      }
    }
    const saved = await addArtifact(type, payload)
    if (saved) {
      resetDraft(type)
      if (!keepOpen) setActiveComposer(null)
    }
  }, [drafts, triage, currentUser, addArtifact, resetDraft])

  const addDraftPerson = useCallback((type, member) => {
    const person = personFromMember(member)
    if (!person) return
    const field = type === 'task' ? 'assignees' : 'askPeople'
    const queryField = type === 'task' ? 'assigneeQuery' : 'askQuery'
    setDrafts(prev => {
      const current = prev[type][field] || []
      if (current.some(existing => personKey(existing) === personKey(person))) {
        return { ...prev, [type]: { ...prev[type], [queryField]: '' } }
      }
      return {
        ...prev,
        [type]: {
          ...prev[type],
          [field]: [...current, person],
          [queryField]: '',
        },
      }
    })
  }, [])

  const removeDraftPerson = useCallback((type, key) => {
    const field = type === 'task' ? 'assignees' : 'askPeople'
    setDrafts(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: (prev[type][field] || []).filter(person => personKey(person) !== key),
      },
    }))
  }, [])

  if (!item) {
    return (
      <div className={`uw-detail-panel${workMode ? ' uw-work-detail-wide' : ''}`}>
        <div className="uw-empty-state">
          <div className="uw-empty-glyph"><IconInbox /></div>
          <div className="uw-empty-title">Select an item</div>
          <div className="uw-empty-desc">Choose an item from the list to view details and take action.</div>
        </div>
      </div>
    )
  }

  const sourceKey = item.source || 'phone_call'
  const SourceBadgeIcon = SOURCE_ICONS[sourceKey] || SOURCE_ICONS.phone_call
  const serviceTypes = item.service_types || []
  const attachments = item.attachments || []
  const tasks = item.tasks || []
  const questions = item.questions || []
  const blockers = item.blockers || []
  const decisions = item.decisions || []
  const openBlockers = blockers.filter(b => b.status !== 'resolved')
  const bidOptions = activeBids
    .filter(b => {
      const q = bidSearch.trim().toLowerCase()
      if (!q) return true
      return String(b.name || '').toLowerCase().includes(q) || String(b.client || '').toLowerCase().includes(q) || String(b.bidNumber || '').toLowerCase().includes(q)
    })
    .slice(0, 10)
  const createdByName = item.created_by
    ? `${item.created_by.firstName || ''} ${item.created_by.lastName || ''}`.trim()
    : 'System'
  const claimedLabel = item.state === 'assigned' ? `Claimed ${item.assigned_at ? timeAgo(item.assigned_at) + ' ago' : ''}` : 'Queued'

  const renderPersonPicker = (type, field, queryField, placeholder) => {
    const draft = drafts[type]
    const selectedPeople = draft[field] || []
    const selectedKeys = new Set(selectedPeople.map(personKey))
    const query = draft[queryField] || ''
    const normalizedQuery = query.trim().toLowerCase()
    const matches = teamMembers
      .filter(member => {
        const person = personFromMember(member)
        if (!person || selectedKeys.has(personKey(person))) return false
        if (!normalizedQuery) return true
        return [
          teamMemberName(member),
          member.email,
          member.title,
          member.division,
        ].filter(Boolean).join(' ').toLowerCase().includes(normalizedQuery)
      })
      .slice(0, 8)

    return (
      <div className="uw-person-picker">
        <div className="uw-person-chip-row">
          {selectedPeople.map(person => (
            <span key={personKey(person)} className="uw-person-chip">
              {person.name}
              <button type="button" onClick={() => removeDraftPerson(type, personKey(person))}>&times;</button>
            </span>
          ))}
          <input
            value={query}
            onChange={e => setDraftValue(type, queryField, e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (matches[0]) addDraftPerson(type, matches[0])
              }
              if (e.key === 'Backspace' && !query && selectedPeople.length) {
                removeDraftPerson(type, personKey(selectedPeople[selectedPeople.length - 1]))
              }
            }}
            placeholder={selectedPeople.length ? 'Add another...' : placeholder}
          />
        </div>
        {normalizedQuery && (
          <div className="uw-person-suggestions">
            {matches.map(member => (
              <button key={member._id} type="button" onMouseDown={() => addDraftPerson(type, member)}>
                <strong>{teamMemberName(member)}</strong>
                {[member.title, member.email].filter(Boolean).length > 0 && <span>{[member.title, member.email].filter(Boolean).join(' / ')}</span>}
              </button>
            ))}
            {matches.length === 0 && <div>No matching team members</div>}
          </div>
        )}
      </div>
    )
  }

  const renderTriageMenu = (field, value, options, className = '') => {
    const normalizedOptions = options.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt)
    const selected = normalizedOptions.find(opt => opt.value === value) || normalizedOptions[0]

    return (
      <span className={`uw-triage-menu ${className}${openTriageMenu === field ? ' open' : ''}`}>
        <button type="button" onClick={() => setOpenTriageMenu(openTriageMenu === field ? null : field)}>
          {selected?.label || value || 'Select'}
          <span className="uw-triage-caret">▾</span>
        </button>
        {openTriageMenu === field && (
          <div className="uw-triage-menu-pop">
            {normalizedOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={opt.value === value ? 'selected' : ''}
                onMouseDown={e => {
                  e.preventDefault()
                  updateTriage(field, opt.value)
                  setOpenTriageMenu(null)
                }}
              >
                <span>{opt.value === value ? '✓' : ''}</span>
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </span>
    )
  }

  const renderComposer = (type) => {
    if (activeComposer !== type) return null
    const draft = drafts[type]
    const titleMap = { task: 'New task', question: 'New question', blocker: 'New blocker', decision: 'New decision' }
    return (
      <div className={`uw-work-composer ${type}`}>
        <div className="uw-work-composer-head">
          <span className="uw-work-composer-icon">{type === 'task' ? 'T' : type === 'question' ? '?' : type === 'blocker' ? '!' : 'D'}</span>
          <span>{titleMap[type]}</span>
          <button className="uw-mini-link" onClick={() => setActiveComposer(null)}>Esc</button>
        </div>

        {type === 'task' && (
          <div className="uw-work-composer-body">
            <textarea autoFocus placeholder="What needs to get done?" value={draft.description} onChange={e => setDraftValue(type, 'description', e.target.value)} />
            <div className="uw-work-field-row">
              {renderPersonPicker(type, 'assignees', 'assigneeQuery', 'Assign to...')}
              <input type="date" value={draft.due_date} onChange={e => setDraftValue(type, 'due_date', e.target.value)} />
              <input placeholder="Tags, comma separated" value={draft.tags} onChange={e => setDraftValue(type, 'tags', e.target.value)} />
            </div>
          </div>
        )}

        {type === 'question' && (
          <div className="uw-work-composer-body">
            <textarea autoFocus placeholder="What do we need to answer?" value={draft.text} onChange={e => setDraftValue(type, 'text', e.target.value)} />
            <div className="uw-work-field-row">
              {renderPersonPicker(type, 'askPeople', 'askQuery', 'Ask...')}
              <input type="date" value={draft.need_by} onChange={e => setDraftValue(type, 'need_by', e.target.value)} />
            </div>
          </div>
        )}

        {type === 'blocker' && (
          <div className="uw-work-composer-body">
            <textarea autoFocus placeholder="What is blocking progress?" value={draft.description} onChange={e => setDraftValue(type, 'description', e.target.value)} />
            <div className="uw-work-field-row">
              <select value={draft.severity} onChange={e => setDraftValue(type, 'severity', e.target.value)}>
                <option value="low">Low severity</option>
                <option value="med">Med severity</option>
                <option value="high">High severity</option>
              </select>
              <input placeholder="Unblock with..." value={draft.unblock_with} onChange={e => setDraftValue(type, 'unblock_with', e.target.value)} />
            </div>
            <input className="uw-full-input" placeholder="Workaround, if any" value={draft.workaround} onChange={e => setDraftValue(type, 'workaround', e.target.value)} />
          </div>
        )}

        {type === 'decision' && (
          <div className="uw-work-composer-body">
            <textarea autoFocus placeholder="What was decided?" value={draft.text} onChange={e => setDraftValue(type, 'text', e.target.value)} />
            <div className="uw-work-field-row">
              <select value={draft.context_question} onChange={e => setDraftValue(type, 'context_question', e.target.value)}>
                <option value="">No linked question</option>
                {questions.map(q => <option key={q._id} value={q._id}>{q.text?.substring(0, 60)}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="uw-work-composer-foot">
          <span>Enter creates. Shift+Enter keeps adding.</span>
          <div>
            <button className="uw-btn uw-btn-ghost uw-btn-sm" onClick={() => setActiveComposer(null)}>Cancel</button>
            <button className="uw-btn uw-btn-primary uw-btn-sm" disabled={loading} onClick={() => submitComposer(type)}>Create</button>
          </div>
        </div>
      </div>
    )
  }

  if (workMode) {
    const shortTitle = item.title || item.body?.substring(0, 72) || 'Work item'
    const createdDate = item.createdAt ? new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
    const completedTasks = tasks.filter(task => task.status === 'done').length
    const answeredQuestions = questions.filter(question => question.status === 'answered').length
    const resolvedBlockers = blockers.filter(blocker => blocker.status === 'resolved').length
    const railTotal = tasks.length + questions.length + blockers.length + decisions.length

    return (
      <div className="uw-work-screen">
        <div className="uw-work-topbar">
          <div className="uw-work-top-main">
            <div className="uw-work-crumbs">
              <Link to="/hmi-v2">HMI</Link>
              <span>›</span>
              <Link to="/unclaimed-work">Work Queue</Link>
              <span>›</span>
              <Link to="/unclaimed-work">Unclaimed Work</Link>
              <span>›</span>
              <span>{shortTitle}</span>
            </div>
            <h1>Working <span>— {shortTitle}</span></h1>
          </div>
          <div className="uw-work-top-status">
            {(triage.priority === 'urgent' || triage.priority === 'high') && <span className={`uw-work-status-pill ${triage.priority}`}>{triage.priority}</span>}
            <span className="uw-work-status-pill claimed">Claimed · {item.assigned_at ? timeAgo(item.assigned_at) + ' ago' : 'now'}</span>
          </div>
          <div className="uw-work-top-actions">
            <button className="uw-btn uw-btn-outline uw-btn-sm" onClick={() => setShowAssignDropdown(!showAssignDropdown)}>Reassign</button>
            <button className="uw-btn uw-btn-outline uw-btn-sm" onClick={handleArchive} disabled={loading}>Archive</button>
            <button className="uw-btn uw-btn-primary" onClick={handleComplete} disabled={loading || openBlockers.length > 0} title={openBlockers.length ? 'Resolve open blockers before completing' : ''}>
              <IconCheck /> Complete & archive
            </button>
          </div>
          {showAssignDropdown && (
            <div className="uw-work-reassign-pop">
              <input
                placeholder="Search team member..."
                value={selectedEmployee ? teamMemberName(selectedEmployee) : assignSearch}
                onChange={e => {
                  if (selectedEmployee) setSelectedEmployee(null)
                  setAssignSearch(e.target.value)
                }}
                autoFocus
              />
              {assignResults.map(emp => (
                <button key={emp._id} type="button" onMouseDown={() => {
                  setSelectedEmployee(emp)
                  setAssignSearch('')
                }}>
                  <strong>{teamMemberName(emp)}</strong>
                  {emp.title && <span>{emp.title}</span>}
                </button>
              ))}
              <div>
                <button className="uw-btn uw-btn-ghost uw-btn-sm" onClick={() => setShowAssignDropdown(false)}>Cancel</button>
                <button className="uw-btn uw-btn-primary uw-btn-sm" disabled={!selectedEmployee || loading} onClick={() => {
                  handleAssign()
                  setShowAssignDropdown(false)
                }}>Assign</button>
              </div>
            </div>
          )}
        </div>

        <div className="uw-work-grid">
          <aside className="uw-work-source-pane">
            <div className="uw-source-head">
              <div className={`uw-source-icon ${sourceKey}`}><SourceBadgeIcon /></div>
              <div>
                <div className="uw-work-overline">{SOURCE_LABELS[sourceKey] || sourceKey} · inbound</div>
                <h2>{item.title || 'Captured work item'}</h2>
              </div>
            </div>
            <div className="uw-source-meta">
              <span className={`uw-chip ${triage.priority === 'urgent' ? 'danger' : 'neutral'}`}>{triage.priority}</span>
              {triage.service && <span className="uw-chip accent">{triage.service}</span>}
              <span>Captured {timeAgo(item.createdAt)} ago</span>
              {createdDate && <span className="mono">{createdDate}</span>}
            </div>

            <div className="uw-source-card">
              <div className="uw-source-section-head">
                <span>Source notes</span>
                <small>{sourceKey}</small>
              </div>
              <div className="uw-source-transcript">
                <p>{item.body || 'No source notes were captured.'}</p>
              </div>
              {attachments.length > 0 && (
                <div className="uw-source-attachments">
                  {attachments.map((att, i) => (
                    <a key={i} className="uw-attach-chip" href={att.url} target="_blank" rel="noopener noreferrer">
                      <IconFile />
                      {att.filename || 'file'}
                      {att.size && <span>{formatSize(att.size)}</span>}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {Array.isArray(item.origin_actions) && item.origin_actions.length > 0 && (
              <div className="uw-source-card">
                <div className="uw-source-section-head">
                  <span>Resolve this item</span>
                  <small>herbicide review</small>
                </div>
                <div className="uw-origin-actions">
                  {item.origin_actions.map((action, index) => (
                    <div key={action.href} className="uw-origin-action">
                      <p>{action.description}</p>
                      {action.kind === 'portal' ? (
                        <Link className={`uw-btn ${index === 0 ? 'uw-btn-primary' : 'uw-btn-outline'} uw-btn-sm`} to={action.href}>{action.label}</Link>
                      ) : (
                        <a className={`uw-btn ${index === 0 ? 'uw-btn-primary' : 'uw-btn-outline'} uw-btn-sm`} href={action.href} target="_blank" rel="noopener noreferrer">{action.label} ↗</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="uw-source-card bare">
              <div className="uw-source-section-head"><span>Identity & routing</span></div>
              <div className="uw-source-facets">
                <div><span>Contact</span><strong>{item.contact_name || 'Not set'}</strong></div>
                <div><span>Caller / sender</span><strong>{createdByName}</strong></div>
                <div><span>Service type</span><strong>{serviceTypes.length ? serviceTypes.join(', ') : triage.service || 'Not set'}</strong></div>
                <div><span>Due date</span><strong>{formatDueDate(item.due_date)}</strong></div>
                <div><span>Assigned to</span><strong>{item.assigned_to_name || triage.owner_name || 'Not assigned'}</strong></div>
                <div><span>Item ID</span><strong className="mono">{String(item._id || '').slice(-8)}</strong></div>
              </div>
            </div>

            <div className="uw-source-card">
              <div className="uw-source-section-head">
                <span>History with contact</span>
                <small>recent</small>
              </div>
              <div className="uw-source-empty">No prior history loaded for this work item.</div>
            </div>
          </aside>

          <main className="uw-work-canvas">
            <div className="uw-work-canvas-inner">
              <div className="uw-work-triage">
                <span>This is a</span>
                {renderTriageMenu('type', triage.type, TRIAGE_TYPES, 'accent')}
                <span>under</span>
                {renderTriageMenu('service', triage.service, TRIAGE_SERVICES)}
                <span>· priority</span>
                {renderTriageMenu('priority', triage.priority, TRIAGE_PRIORITIES, triage.priority === 'urgent' || triage.priority === 'high' ? 'danger' : '')}
                <span>· owner</span>
                <input value={triage.owner_name || ''} onChange={e => updateTriage('owner_name', e.target.value)} placeholder="Owner" />
                <span>· due</span>
                <input className="purple" type="date" value={triage.due_date || ''} onChange={e => updateTriage('due_date', e.target.value)} />
              </div>

              <div className="uw-work-notes-panel">
                <div className="uw-source-section-head">
                  <span>Working notes</span>
                  <small>Visible to team</small>
                </div>
                <textarea
                  value={workNotes}
                  onChange={e => setWorkNotes(e.target.value)}
                  maxLength={5000}
                  placeholder="What did you find? What's the context? Notes are visible to everyone on the team..."
                />
                {attachments.length > 0 && (
                  <div className="uw-work-attach-row">
                    {attachments.map((att, i) => (
                      <a key={i} className="uw-attach-chip" href={att.url} target="_blank" rel="noopener noreferrer">
                        <IconFile />
                        {att.filename || 'file'}
                        {att.size && <span>{formatSize(att.size)}</span>}
                      </a>
                    ))}
                  </div>
                )}
                <div className="uw-work-notes-foot">
                  <span>{savingNotes ? 'Saving...' : lastSavedAt ? `Auto-saved · ${timeAgo(lastSavedAt)} ago` : 'Not saved yet'}</span>
                  <span>{workNotes.length} / 5000 chars</span>
                </div>
              </div>

              <div className="uw-work-bid-inline">
                {item.bid_project ? (
                  <>
                    <span>Bid project</span>
                    <strong>{item.bid_project_name || 'Linked bid project'}</strong>
                    <a href={`/bid-estimation/${item.bid_project}`}>Open</a>
                  </>
                ) : (
                  <>
                    <span>Bid project</span>
                    <div>
                      <input
                        value={selectedBid ? `${selectedBid.name}${selectedBid.client ? ' · ' + selectedBid.client : ''}` : bidSearch}
                        onChange={e => {
                          if (selectedBid) setSelectedBid(null)
                          setBidSearch(e.target.value)
                          setShowBidDropdown(true)
                        }}
                        onFocus={() => setShowBidDropdown(true)}
                        onBlur={() => setTimeout(() => setShowBidDropdown(false), 200)}
                        placeholder="Search active bid projects..."
                      />
                      {showBidDropdown && !selectedBid && (
                        <div className="uw-bid-dropdown">
                          {bidOptions.map(bid => (
                            <button
                              key={bid._id}
                              type="button"
                              onMouseDown={() => {
                                setSelectedBid(bid)
                                setBidSearch('')
                                setShowBidDropdown(false)
                              }}
                            >
                              <strong>{bid.name}</strong>
                              {[bid.client, bid.bidNumber, bid.status].filter(Boolean).length > 0 && (
                                <span>{[bid.client, bid.bidNumber, bid.status].filter(Boolean).join(' · ')}</span>
                              )}
                            </button>
                          ))}
                          {bidOptions.length === 0 && <div className="uw-bid-empty">No matching active bids</div>}
                        </div>
                      )}
                    </div>
                    <button className="uw-btn uw-btn-primary uw-btn-sm" onClick={handleLinkBid} disabled={!selectedBid || loading}>Link</button>
                  </>
                )}
              </div>

              {item.state !== 'completed' && item.state !== 'archived' && (
                <>
                  <div className="uw-work-action-row">
                    <button className="task" onClick={() => setActiveComposer(activeComposer === 'task' ? null : 'task')}>Create task <span>⌘T</span></button>
                    <button className="question" onClick={() => setActiveComposer(activeComposer === 'question' ? null : 'question')}>Ask a question <span>⌘Q</span></button>
                    <button className="blocker" onClick={() => setActiveComposer(activeComposer === 'blocker' ? null : 'blocker')}>Flag a blocker <span>⌘B</span></button>
                    <button className="attach" type="button">Attach</button>
                  </div>
                  {renderComposer('task')}
                  {renderComposer('question')}
                  {renderComposer('blocker')}
                  {questions.length > 0 && renderComposer('decision')}
                </>
              )}

              <div className="uw-work-actionbar">
                <div>
                  <b>{tasks.length}</b> tasks
                  <span>·</span>
                  <b>{questions.length}</b> questions
                  <span>·</span>
                  <b>{openBlockers.length}</b> blocker open
                  <span>·</span>
                  <b>{decisions.length}</b> decisions
                </div>
                <Link className="uw-btn uw-btn-outline" to="/unclaimed-work">Save & close</Link>
                <button className="uw-btn uw-btn-outline" onClick={() => setShowAssignDropdown(true)}>Hand off</button>
                <button className="uw-btn uw-btn-primary" onClick={handleComplete} disabled={loading || openBlockers.length > 0}>
                  <IconCheck /> Complete & archive
                </button>
              </div>
            </div>
          </main>

          <aside className="uw-work-items-rail">
            <div className="uw-work-rail-head">
              <h2>Items</h2>
              <span>{railTotal} created</span>
            </div>

            <div className="uw-work-rail-section tasks">
              <div className="uw-work-rail-section-head"><span>Tasks</span><small>{tasks.length} · {completedTasks} done</small></div>
              {tasks.length === 0 && <div className="uw-rail-empty">No tasks yet.</div>}
              {tasks.map(task => (
                <div key={task._id} className={`uw-work-rail-card ${task.status === 'done' ? 'done' : ''}`}>
                  <button className="mark" title={task.status === 'done' ? 'Reopen task' : 'Mark task done'} onClick={() => updateArtifact('task', task._id, { status: task.status === 'done' ? 'open' : 'done' })}>{task.status === 'done' ? '✓' : ''}</button>
                  <div>
                    <strong>{task.description}</strong>
                    <span>{artifactPeopleLabel(task, 'assignee_names', 'assignee_name', triage.owner_name || 'Owner')}{task.due_date ? ` · ${formatDueDate(task.due_date)}` : ''}</span>
                    <div className="uw-work-rail-actions">
                      <button onClick={() => updateArtifact('task', task._id, { status: task.status === 'done' ? 'open' : 'done' })}>{task.status === 'done' ? 'Reopen' : 'Done'}</button>
                      <button onClick={() => {
                        const description = window.prompt('Edit task', task.description || '')
                        if (description?.trim()) updateArtifact('task', task._id, { description: description.trim() })
                      }}>Edit</button>
                      <button className="danger" onClick={() => deleteArtifact('task', task._id)}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="uw-work-rail-section questions">
              <div className="uw-work-rail-section-head"><span>Questions</span><small>{questions.length} · {answeredQuestions} answered</small></div>
              {questions.length === 0 && <div className="uw-rail-empty">No questions yet.</div>}
              {questions.map(question => (
                <div key={question._id} className={`uw-work-rail-card question ${question.status === 'answered' ? 'done' : ''}`}>
                  <button
                    className="mark"
                    title={question.status === 'answered' ? 'Reopen question' : 'Answer question'}
                    onClick={() => {
                      if (question.status === 'answered') {
                        updateArtifact('question', question._id, { status: 'open', answer_text: '' })
                        return
                      }
                      const answer = window.prompt('Answer this question')
                      if (answer) updateArtifact('question', question._id, { status: 'answered', answer_text: answer })
                    }}
                  >?</button>
                  <div>
                    <strong>{question.text}</strong>
                    <span>{artifactPeopleLabel(question, 'ask_names', 'ask', 'Ask not set')} · {question.status === 'answered' ? 'answered' : 'waiting'}</span>
                    {question.answer_text && <em>{question.answer_text}</em>}
                    {question.answer_text && (
                      <button
                        className="uw-log-decision-link"
                        onClick={() => {
                          setDrafts(prev => ({
                            ...prev,
                            decision: { text: question.answer_text, context_question: question._id },
                          }))
                          setActiveComposer('decision')
                        }}
                      >Log decision</button>
                    )}
                    <div className="uw-work-rail-actions">
                      <button onClick={() => {
                        if (question.status === 'answered') {
                          updateArtifact('question', question._id, { status: 'open', answer_text: '' })
                          return
                        }
                        const answer = window.prompt('Answer this question')
                        if (answer) updateArtifact('question', question._id, { status: 'answered', answer_text: answer })
                      }}>{question.status === 'answered' ? 'Reopen' : 'Answer'}</button>
                      <button onClick={() => {
                        const text = window.prompt('Edit question', question.text || '')
                        if (text?.trim()) updateArtifact('question', question._id, { text: text.trim() })
                      }}>Edit</button>
                      <button className="danger" onClick={() => deleteArtifact('question', question._id)}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="uw-work-rail-section blockers">
              <div className="uw-work-rail-section-head"><span>Blockers</span><small>{openBlockers.length} open · {resolvedBlockers} resolved</small></div>
              {blockers.length === 0 && <div className="uw-rail-empty">No blockers.</div>}
              {blockers.map(blocker => (
                <div key={blocker._id} className={`uw-work-rail-card blocker ${blocker.status === 'resolved' ? 'done' : ''}`}>
                  <button className="mark" title={blocker.status === 'resolved' ? 'Reopen blocker' : 'Resolve blocker'} onClick={() => updateArtifact('blocker', blocker._id, { status: blocker.status === 'resolved' ? 'open' : 'resolved' })}>!</button>
                  <div>
                    <strong>{blocker.description}</strong>
                    <span>{blocker.status === 'resolved' ? 'Resolved' : `Sev ${blocker.severity || 'med'}`}{blocker.unblock_with ? ` · ${blocker.unblock_with}` : ''}</span>
                    {blocker.workaround && <em>{blocker.workaround}</em>}
                    <div className="uw-work-rail-actions">
                      <button onClick={() => updateArtifact('blocker', blocker._id, { status: blocker.status === 'resolved' ? 'open' : 'resolved' })}>{blocker.status === 'resolved' ? 'Reopen' : 'Resolve'}</button>
                      <button onClick={() => {
                        const description = window.prompt('Edit blocker', blocker.description || '')
                        if (description?.trim()) updateArtifact('blocker', blocker._id, { description: description.trim() })
                      }}>Edit</button>
                      <button className="danger" onClick={() => deleteArtifact('blocker', blocker._id)}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {questions.length > 0 && (
              <div className="uw-work-rail-section decisions">
                <div className="uw-work-rail-section-head"><span>Decisions</span><small>{decisions.length}</small></div>
                {decisions.length === 0 && <div className="uw-rail-empty">No decisions logged.</div>}
                {decisions.map(decision => (
                  <div key={decision._id} className="uw-work-rail-card decision done">
                    <span className="mark">✓</span>
                    <div>
                      <strong>{decision.text}</strong>
                      <span>{decision.decided_by_name || triage.owner_name || 'Owner'} · {decision.decided_at ? timeAgo(decision.decided_at) + ' ago' : 'now'}</span>
                      <div className="uw-work-rail-actions">
                        <button onClick={() => {
                          const text = window.prompt('Edit decision', decision.text || '')
                          if (text?.trim()) updateArtifact('decision', decision._id, { text: text.trim() })
                        }}>Edit</button>
                        <button className="danger" onClick={() => deleteArtifact('decision', decision._id)}>Remove</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    )
  }

  return (
    <div className={`uw-detail-panel uw-work-detail-panel${workMode ? ' uw-work-detail-wide' : ''}`}>
      <div className="uw-detail-head">
        <div className="uw-detail-title">{item.title || item.body?.substring(0, 80) || 'Untitled'}</div>
        <div className="uw-detail-meta-row">
          <span className={`uw-detail-source-badge ${sourceKey}`}><SourceBadgeIcon />{SOURCE_LABELS[sourceKey] || sourceKey}</span>
          {triage.priority === 'urgent' && <span className="uw-pill uw-pill-urgent">Urgent</span>}
          <span className={`uw-work-state ${item.state}`}>{claimedLabel}</span>
          <span className="uw-detail-time">{timeAgo(item.createdAt)} ago by {createdByName}</span>
        </div>
      </div>

      <div className="uw-detail-body">
        {item.body && (
          <div className="uw-transcript-block">
            <p>{item.body}</p>
          </div>
        )}

        <div className="uw-info-grid">
          <div>
            <div className="uw-info-label">Contact</div>
            <div className="uw-info-value">{item.contact_name ? <span className="contact-link">{item.contact_name}</span> : <span style={{ color: 'var(--ink-4)' }}>Not set</span>}</div>
          </div>
          <div>
            <div className="uw-info-label">Service Type</div>
            <div className="uw-info-value">{serviceTypes.length > 0 ? serviceTypes.join(', ') : 'Not set'}</div>
          </div>
          <div>
            <div className="uw-info-label">Due Date</div>
            <div className="uw-info-value">{formatDueDate(item.due_date)}</div>
          </div>
          <div>
            <div className="uw-info-label">Assigned To</div>
            <div className="uw-info-value">{item.assigned_to_name || triage.owner_name || 'Not assigned'}</div>
          </div>
        </div>

        {attachments.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div className="uw-info-label" style={{ marginBottom: 6 }}>Attachments</div>
            <div className="uw-attachments">
              {attachments.map((att, i) => (
                <a key={i} className="uw-att-chip" href={att.url} target="_blank" rel="noopener noreferrer">
                  <IconFile />
                  {att.filename || 'file'}
                  {att.size && <span className="att-size">{formatSize(att.size)}</span>}
                </a>
              ))}
            </div>
          </div>
        )}

        {workMode && (
          <div className="uw-bid-link-box">
            <div>
              <div className="uw-work-section-title">Bid Project</div>
              {item.bid_project ? (
                <div className="uw-linked-bid">
                  <span>{item.bid_project_name || 'Linked bid project'}</span>
                  <a href={`/bid-estimation/${item.bid_project}`}>Open bid context</a>
                </div>
              ) : (
                <div className="uw-bid-picker">
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      value={selectedBid ? `${selectedBid.name}${selectedBid.client ? ' · ' + selectedBid.client : ''}` : bidSearch}
                      onChange={e => {
                        if (selectedBid) setSelectedBid(null)
                        setBidSearch(e.target.value)
                        setShowBidDropdown(true)
                      }}
                      onFocus={() => setShowBidDropdown(true)}
                      onBlur={() => setTimeout(() => setShowBidDropdown(false), 200)}
                      placeholder="Search active bid projects..."
                    />
                    {showBidDropdown && !selectedBid && (
                      <div className="uw-bid-dropdown">
                        {bidOptions.map(bid => (
                          <button
                            key={bid._id}
                            type="button"
                            onMouseDown={() => {
                              setSelectedBid(bid)
                              setBidSearch('')
                              setShowBidDropdown(false)
                            }}
                          >
                            <strong>{bid.name}</strong>
                            {[bid.client, bid.bidNumber, bid.status].filter(Boolean).length > 0 && (
                              <span>{[bid.client, bid.bidNumber, bid.status].filter(Boolean).join(' · ')}</span>
                            )}
                          </button>
                        ))}
                        {bidOptions.length === 0 && (
                          <div className="uw-bid-empty">No matching active bids</div>
                        )}
                      </div>
                    )}
                  </div>
                  <button className="uw-btn uw-btn-primary uw-btn-sm" onClick={handleLinkBid} disabled={!selectedBid || loading}>
                    Link bid
                  </button>
                </div>
              )}
            </div>
            <div className="uw-bid-link-note">
              Linked work item tasks, questions, blockers, and decisions appear in the bid project context.
            </div>
          </div>
        )}

        {workMode && item.state !== 'completed' && item.state !== 'archived' && (
          <div className="uw-work-surface">
            <div className="uw-work-section-title">Work It</div>
            <div className="uw-triage-sentence">
              <span>This is a</span>
              <select value={triage.type} onChange={e => updateTriage('type', e.target.value)}>
                {TRIAGE_TYPES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <span>under</span>
              <select value={triage.service} onChange={e => updateTriage('service', e.target.value)}>
                {TRIAGE_SERVICES.map(service => <option key={service} value={service}>{service}</option>)}
              </select>
              <span>priority</span>
              <select className={`priority-${triage.priority}`} value={triage.priority} onChange={e => updateTriage('priority', e.target.value)}>
                {TRIAGE_PRIORITIES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <span>owner</span>
              <input value={triage.owner_name || ''} onChange={e => updateTriage('owner_name', e.target.value)} placeholder="Owner" />
              <span>due</span>
              <input type="date" value={triage.due_date || ''} onChange={e => updateTriage('due_date', e.target.value)} />
            </div>

            <div className="uw-notes-box">
              <textarea
                value={workNotes}
                onChange={e => setWorkNotes(e.target.value)}
                maxLength={5000}
                placeholder="Working notes, findings, call details, supplier info..."
              />
              <div className="uw-notes-foot">
                <span>{savingNotes ? 'Saving...' : lastSavedAt ? `Saved ${timeAgo(lastSavedAt)} ago` : 'Not saved yet'}</span>
                <span>{workNotes.length}/5000</span>
              </div>
            </div>

            <div className="uw-create-row">
              <button className="uw-create-btn task" onClick={() => setActiveComposer(activeComposer === 'task' ? null : 'task')}>Create task</button>
              <button className="uw-create-btn question" onClick={() => setActiveComposer(activeComposer === 'question' ? null : 'question')}>Ask question</button>
              <button className="uw-create-btn blocker" onClick={() => setActiveComposer(activeComposer === 'blocker' ? null : 'blocker')}>Flag blocker</button>
            </div>
            {renderComposer('task')}
            {renderComposer('question')}
            {renderComposer('blocker')}
            {questions.length > 0 && (
              <>
                <button className="uw-create-btn decision" style={{ width: '100%' }} onClick={() => setActiveComposer(activeComposer === 'decision' ? null : 'decision')}>Log decision</button>
                {renderComposer('decision')}
              </>
            )}
          </div>
        )}

        {workMode && (
        <div className="uw-artifact-rail">
          <div className="uw-artifact-head">
            <div className="uw-work-section-title">Items Created</div>
            <span>{tasks.length + questions.length + blockers.length + decisions.length} total</span>
          </div>

          <div className="uw-rail-section tasks">
            <div className="uw-rail-section-head"><span>Tasks</span><small>{sectionCount(tasks, 'done')}</small></div>
            {tasks.length === 0 && <div className="uw-rail-empty">No tasks yet.</div>}
            {tasks.map(task => (
              <div key={task._id} className={`uw-rail-item ${task.status === 'done' ? 'done' : ''}`}>
                <button className="uw-rail-mark" onClick={() => updateArtifact('task', task._id, { status: task.status === 'done' ? 'open' : 'done' })}>{task.status === 'done' ? '✓' : ''}</button>
                <div>
                  <div className="uw-rail-desc">{task.description}</div>
                  <div className="uw-rail-meta">{artifactPeopleLabel(task, 'assignee_names', 'assignee_name', triage.owner_name || 'Owner')}{task.due_date ? ` / ${formatDueDate(task.due_date)}` : ''}</div>
                  <button className="uw-rail-remove" onClick={() => deleteArtifact('task', task._id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <div className="uw-rail-section questions">
            <div className="uw-rail-section-head"><span>Questions</span><small>{sectionCount(questions, 'answered')}</small></div>
            {questions.length === 0 && <div className="uw-rail-empty">No questions yet.</div>}
            {questions.map(question => (
              <div key={question._id} className={`uw-rail-item ${question.status === 'answered' ? 'done' : ''}`}>
                <button
                  className="uw-rail-mark question"
                  onClick={() => {
                    if (question.status === 'answered') {
                      updateArtifact('question', question._id, { status: 'open', answer_text: '' })
                      return
                    }
                    const answer = window.prompt('Answer this question')
                    if (answer) updateArtifact('question', question._id, { status: 'answered', answer_text: answer })
                  }}
                >?</button>
                <div>
                  <div className="uw-rail-desc">{question.text}</div>
                  <div className="uw-rail-meta">{artifactPeopleLabel(question, 'ask_names', 'ask', 'Ask not set')} / {question.status === 'answered' ? 'answered' : 'waiting'}</div>
                  {question.answer_text && (
                    <div className="uw-answer-block">
                      {question.answer_text}
                      <button
                        onClick={() => {
                          setDrafts(prev => ({
                            ...prev,
                            decision: { text: question.answer_text, context_question: question._id },
                          }))
                          setActiveComposer('decision')
                        }}
                      >Log decision</button>
                    </div>
                  )}
                  <button className="uw-rail-remove" onClick={() => deleteArtifact('question', question._id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <div className="uw-rail-section blockers">
            <div className="uw-rail-section-head"><span>Blockers</span><small>{sectionCount(blockers, 'resolved')}</small></div>
            {blockers.length === 0 && <div className="uw-rail-empty">No blockers.</div>}
            {blockers.map(blocker => (
              <div key={blocker._id} className={`uw-rail-item ${blocker.status === 'resolved' ? 'done' : ''}`}>
                <button className="uw-rail-mark blocker" onClick={() => updateArtifact('blocker', blocker._id, { status: blocker.status === 'resolved' ? 'open' : 'resolved' })}>!</button>
                <div>
                  <div className="uw-rail-desc">{blocker.description}</div>
                  <div className="uw-rail-meta">Severity {blocker.severity || 'med'}{blocker.unblock_with ? ` / ${blocker.unblock_with}` : ''}</div>
                  {blocker.workaround && <div className="uw-answer-block">{blocker.workaround}</div>}
                  <button className="uw-rail-remove" onClick={() => deleteArtifact('blocker', blocker._id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          {questions.length > 0 && (
            <div className="uw-rail-section decisions">
              <div className="uw-rail-section-head"><span>Decisions</span><small>{decisions.length}</small></div>
              {decisions.length === 0 && <div className="uw-rail-empty">No decisions logged.</div>}
              {decisions.map(decision => (
                <div key={decision._id} className="uw-rail-item done">
                  <span className="uw-rail-mark decision">✓</span>
                  <div>
                    <div className="uw-rail-desc">{decision.text}</div>
                    <div className="uw-rail-meta">{decision.decided_by_name || triage.owner_name || 'Owner'} / {decision.decided_at ? timeAgo(decision.decided_at) + ' ago' : 'now'}</div>
                    <button className="uw-rail-remove" onClick={() => deleteArtifact('decision', decision._id)}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {item.state !== 'completed' && item.state !== 'archived' && (
        <div className="uw-action-section">
          <div className="uw-action-section-title">Actions</div>
          <div className="uw-assign-row">
            <input
              className="uw-assign-input"
              placeholder="Search team member to assign..."
              value={selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : assignSearch}
              onChange={e => {
                if (selectedEmployee) setSelectedEmployee(null)
                setAssignSearch(e.target.value)
              }}
              onFocus={() => assignResults.length > 0 && setShowAssignDropdown(true)}
              onBlur={() => setTimeout(() => setShowAssignDropdown(false), 200)}
            />
            <button className="uw-btn uw-btn-outline uw-btn-sm" onClick={handleAssign} disabled={!selectedEmployee || loading}>Assign</button>
            {showAssignDropdown && assignResults.length > 0 && (
              <div className="uw-employee-dropdown">
                {assignResults.map(emp => (
                  <div key={emp._id} className="uw-employee-option" onMouseDown={() => {
                    setSelectedEmployee(emp)
                    setAssignSearch('')
                    setShowAssignDropdown(false)
                  }}>
                    <span className="uw-employee-name">{emp.firstName} {emp.lastName}</span>
                    {emp.title && <span className="uw-employee-role">{emp.title}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="uw-action-row">
            <button className="uw-btn uw-btn-accent" style={{ flex: 1 }} onClick={handleClaim} disabled={loading}><IconClaim /> Claim</button>
            {workMode ? (
              <button className="uw-btn uw-btn-ok" style={{ flex: 1 }} onClick={handleComplete} disabled={loading || openBlockers.length > 0} title={openBlockers.length ? 'Resolve open blockers before completing' : ''}><IconCheck /> Complete</button>
            ) : (
              <Link
                className={`uw-btn uw-btn-primary${item.state === 'submitted' ? ' disabled' : ''}`}
                style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
                to={item.state === 'submitted' ? '#' : `/unclaimed-work/${item._id}/work`}
                onClick={e => { if (item.state === 'submitted') e.preventDefault() }}
                title={item.state === 'submitted' ? 'Claim or assign this item before working it' : 'Open work surface'}
              >
                Work item
              </Link>
            )}
          </div>

          <div className="uw-aspire-row">
            <input className="uw-aspire-input" placeholder="Aspire Work Ticket ID" value={aspireId} onChange={e => setAspireId(e.target.value)} />
            <button className="uw-btn uw-btn-ghost uw-btn-sm" onClick={handleLinkAspire} disabled={!aspireId.trim() || loading}>Link</button>
          </div>

          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button className="uw-btn uw-btn-ghost uw-btn-sm" style={{ color: 'var(--ink-4)' }} onClick={handleArchive} disabled={loading}><IconTrash /> Archive</button>
          </div>
        </div>
      )}

      {item.state === 'completed' && (
        <div className="uw-action-section">
          <div className="uw-action-section-title">Completed</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>This item has been marked as completed.</div>
        </div>
      )}
    </div>
  )
}
